import { useState, useRef, useEffect } from 'react';
import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import triggerTracking from '@blueprint/tracking';
import {
    SocialSignInGoogleDocument,
    SocialSignInGoogleMutation,
    SocialSignInGoogleMutationVariables
} from '../../../queries/googleSignIn.generated';
import {
    FetchUserWithTrackedPropertiesDocument,
    FetchUserWithTrackedPropertiesQuery,
    FetchUserWithTrackedPropertiesQueryVariables
} from '../../../queries/userWithTrackedProperties.generated';
import { triggerSuccessfulAuthentication } from '../../../../../utils/triggerSuccessfulAuthentication';
import { SignInAction } from '../useSocialSignIn.types';
import { loadGoogleSdk } from './loadGoogleSdk';
import { isNewUser } from '../../../../../utils';
import { User } from '../../../../../../../graphql.generated';
import { AuthModalTracking } from '../../../../../AuthModal.types';
import { authTaxonomyObject } from '../../../../../constants/taxonomy';
import { trackAuthSegmentEvent } from '../../../../../utils/segmentTrack';
import { formatNewRelicErrorData } from '../../../../../utils/formatNewRelicErrorData';
import { formatNewRelicSuccessData } from '../../../../../utils/formatNewRelicSuccessData';
import { AuthToken, WithOAuth2Props } from './useGoogle.types';
import { setActiveSession } from '@zoopla/user';

export const useGoogle = (
    { onSuccess, onError, eventLabel, segment }: SignInAction,
    currentUser: Pick<User, 'registered'> = {}
) => {
    const [isGoogleSDKLoaded, setIsGoogleSDKLoaded] = useState(false);
    const [isGoogleAuthenticating, setIsGoogleAuthenticating] = useState(false);

    const googleCallbackRef =
        useRef<
            ({
                accessToken,
                rememberMe
            }: {
                accessToken: string;
                rememberMe: boolean;
            }) => void
        >();

    useEffect(() => {
        googleCallbackRef.current = signInMutation;
    });

    const { lazyFetch: triggerSocialUserTracking } = useLazyFetchGraphql<
        FetchUserWithTrackedPropertiesQuery,
        FetchUserWithTrackedPropertiesQueryVariables
    >(FetchUserWithTrackedPropertiesDocument, {
        async onCompleted(data) {
            if (data?.user?.registrationSources) {
                const isGoogleOnly =
                    data.user.registrationSources.length === 1 &&
                    data.user.registrationSources[0] === 'google';

                triggerTracking<AuthModalTracking>({
                    event: 'GAEvent',
                    eventCategory:
                        isGoogleOnly &&
                        isNewUser(currentUser, data.user.creationDate)
                            ? 'registration'
                            : 'signin',
                    eventAction: 'success - google',
                    eventLabel: eventLabel || 'sign_in_modal',
                    taxonomy: authTaxonomyObject.google.signin
                });

                await trackAuthSegmentEvent(
                    isGoogleOnly &&
                        isNewUser(currentUser, data.user.creationDate)
                        ? 'reg'
                        : 'signin',
                    segment,
                    data.user
                );
            }
            // put onSuccess() here as it unmounts the modal component, caused error: `Can't perform a React state update on an unmounted component`
            onSuccess(data?.user || undefined);
        },
        onError() {
            // The user query starts when sign in mutation is completed successfully.
            // User is already logged in at this moment. Trigger onSuccess here to close the authModal.
            onSuccess();
        }
    });

    const { lazyFetch: signInMutation } = useLazyFetchGraphql<
        SocialSignInGoogleMutation,
        SocialSignInGoogleMutationVariables
    >(SocialSignInGoogleDocument, {
        onCompleted({ socialSignInGoogle }) {
            if (!socialSignInGoogle?.success) {
                onError('Sorry, something went wrong. Please try again.');
                setIsGoogleAuthenticating(false);
                triggerTracking({
                    event: 'GAEvent',
                    eventCategory: 'signin',
                    eventAction: 'failure_generic',
                    eventLabel: eventLabel || 'sign_in_modal'
                });
                return;
            }
            setActiveSession();
            triggerSuccessfulAuthentication();
            triggerSocialUserTracking();
            setIsGoogleAuthenticating(false);
        },
        onError() {
            onError('Sorry, something went wrong. Please try again.');
            setIsGoogleAuthenticating(false);
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'signin',
                eventAction: 'failure_generic',
                eventLabel: eventLabel || 'sign_in_modal'
            });
        }
    });

    const signInToZooplaWithGoogle = (
        credentialResponse: AuthToken,
        rememberMe: boolean
    ) => {
        if (credentialResponse) {
            setIsGoogleAuthenticating(true);
            googleCallbackRef?.current?.({
                accessToken: credentialResponse.access_token,
                rememberMe
            });

            window.newrelic?.addPageAction(
                ...formatNewRelicSuccessData({
                    name: 'google-auth'
                })
            );
        } else {
            window.newrelic?.addPageAction(
                ...formatNewRelicErrorData({
                    name: 'google-auth',
                    type: 'missing-token'
                })
            );
        }
    };

    const init = () => {
        loadGoogleSdk();
        setIsGoogleSDKLoaded(true);
    };

    const onGoogleSigninError = () => {
        onError(
            'Something went wrong and you have not been signed in. Please try again.'
        );

        setIsGoogleAuthenticating(false);

        window.newrelic?.addPageAction(
            ...formatNewRelicErrorData({
                name: 'google-auth',
                type: 'immediate_failed'
            })
        );

        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'signin',
            eventAction: 'failure_google',
            eventLabel: eventLabel || 'sign_in_modal'
        });
    };

    const onLogin = (rememberMe?: boolean) => {
        if (!isGoogleSDKLoaded) {
            init();
        }

        try {
            (window.google as unknown as WithOAuth2Props).accounts?.oauth2
                .initTokenClient({
                    client_id:
                        '696203622959-7kq1qaikjuk02838oc5ckv0t3a4329tq.apps.googleusercontent.com',
                    ux_mode: 'popup',
                    scope: 'https://www.googleapis.com/auth/userinfo.email',
                    callback: (response) =>
                        signInToZooplaWithGoogle(response, rememberMe || false)
                })
                ?.requestAccessToken?.();
        } catch (e: unknown) {
            onGoogleSigninError();
        }
    };

    return {
        init,
        onLogin,
        isAuthenticating: isGoogleAuthenticating
    };
};
